import { useQuery } from "@tanstack/react-query";
import api from "api";
import dates from "core/dates";


const formatX = (minutes: number, ms: number = 0) => dates.set(dates.startOfDay(new Date()), { minutes, milliseconds: ms }).getTime();

const refetch = (last: string | undefined, offsetMinutes: number = 10) => {
	if (!last) return false;

	const next = dates.add.minutes(new Date(last), offsetMinutes).getTime();
	const ms = next - Date.now();
	return ms;
};

const DAY_IN_MIN = 1440;
const HOUR_IN_MIN = 60;

export const useQueryDAU = () => useQuery({
	queryKey: ["dau"],
	queryFn: () => api.admin.reporting.dau({ timeZone: dates.getTimezone() }),
	select: (data) => ({
		series: [
			{ name: "Yesterday", data: data.yesterday.map(({ x, y }) => ({ x: formatX(x), y })) },
			{ name: "Today", data: data.today.map(({ x, y }) => ({ x: formatX(x, 1), y })) } // slight offset to distinguish points (apexcharts bug)
		],
		count: data.count,
		diff: data.diff,
		last: data.last
	}),
	refetchInterval: (query) => refetch(query.state.data?.last),
	refetchIntervalInBackground: true
});

export const useQueryMAU = () => useQuery({
	queryKey: ["mau"],
	queryFn: () => api.admin.reporting.rmau({ timeZone: dates.getTimezone() }),
	select: (data) => ({
		series: [{ name: "Last 30 Days", data: data.data }],
		count: data.count,
		last: data.last
	}),
	refetchInterval: (query) => refetch(query.state.data?.last, DAY_IN_MIN),
	refetchIntervalInBackground: true
});

export const useQueryTotalAccounts = () => useQuery({
	queryKey: ["totalAccounts"],
	queryFn: () => api.admin.reporting.ta({ timeZone: dates.getTimezone() }),
	select: (data) => ({
		series: [{ name: "Unfunded", data: data.unfunded }, { name: "Funded", data: data.funded }],
		count: data.count,
		last: data.last
	}),
	refetchInterval: (query) => refetch(query.state.data?.last, HOUR_IN_MIN),
	refetchIntervalInBackground: true
});

export const useQueryRevenue = () => useQuery({
	queryKey: ["revenue"],
	queryFn: () => api.admin.reporting.revenue({ timeZone: dates.getTimezone() }),
	select: (data) => ({
		series: [
			{ name: "Yesterday", data: data.yesterday.map(({ x, y }) => ({ x: formatX(x), y })) },
			{ name: "Today", data: data.today.map(({ x, y }) => ({ x: formatX(x, 1), y })) }
		],
		count: data.total,
		last: data.last
	}),
	refetchInterval: (query) => refetch(query.state.data?.last, HOUR_IN_MIN),
	refetchIntervalInBackground: true
});

export const useQueryFloat = () => useQuery({
	queryKey: ["float"],
	queryFn: () => api.admin.reporting.float({ timeZone: dates.getTimezone() }),
	select: (data) => ({
		...data,
		count: {
			value: data.tokens * data.tokenRate,
			max: data.maxTokens * data.tokenRate,
			min: data.minTokens * data.tokenRate
		}
	}),
	refetchInterval: (query) => refetch(query.state.data?.last, DAY_IN_MIN),
	refetchIntervalInBackground: true
});

export const useQueryMonetizedTraffic = () => useQuery({
	queryKey: ["mntr"],
	queryFn: () => api.admin.reporting.monetizedTraffic({ timeZone: dates.getTimezone() }),
	select: (data) => ({
		...data,
		count: {
			value: data.rate / 100,
			max: data.maxRate / 100,
			min: data.minRate / 100
		}
	}),
	refetchInterval: (query) => refetch(query.state.data?.last, HOUR_IN_MIN),
	refetchIntervalInBackground: true
});